import axiosInstance from 'src/common/axiosInstance';

// AUTO-CRM-32: shared helpers for day-to-day CRM work (follow-ups, stage moves,
// quick logging). Every call uses an existing CRM endpoint.

export const ACTIVITY_TYPES = {
  CALL: { label: 'Call', verb: 'Call' },
  WHATSAPP: { label: 'WhatsApp', verb: 'Message on WhatsApp' },
  MEETING: { label: 'Meeting', verb: 'Meet' },
  EMAIL: { label: 'Email', verb: 'Email' },
  FOLLOWUP: { label: 'Follow-up', verb: 'Follow up with' },
  NOTE: { label: 'Note', verb: 'Note on' },
};

// ---- follow-ups -------------------------------------------------------------

export async function listFollowUps({ mine = false, type = '', scheduledFrom, scheduledTo, perPage = 100, page = 1 } = {}) {
  const params = { status: 'PENDING', sort: 'scheduled', per_page: perPage, page };
  if (mine) params.assigned_to = 'me';
  if (type) params.type = type;
  if (scheduledFrom) params.scheduled_from = scheduledFrom.toISOString();
  if (scheduledTo) params.scheduled_to = scheduledTo.toISOString();
  const response = await axiosInstance.get('/lead-activities', { params });
  const rows = response?.data?.data;
  return { items: Array.isArray(rows) ? rows : [], meta: response?.data?.meta || {} };
}

export async function countDueFollowUps({ mine = true } = {}) {
  const { meta, items } = await listFollowUps({ mine, scheduledTo: endOfDay(new Date()), perPage: 1 });
  return Number(meta.total ?? items.length) || 0;
}

export async function nextFollowUpForLead(leadId) {
  const response = await axiosInstance.get('/lead-activities', { params: { lead_id: leadId, status: 'PENDING', sort: 'scheduled', per_page: 5 } });
  const rows = response.data?.data || [];
  return rows.find((row) => row.scheduled_at) || rows[0] || null;
}

export const completeActivity = (id) => axiosInstance.patch(`/lead-activities/${id}/complete`);
export const rescheduleActivity = (id, when) => axiosInstance.patch(`/lead-activities/${id}`, { scheduled_at: when.toISOString() });

export function createActivity({ leadId, type, note = '', when = null, outcome = null }) {
  return axiosInstance.post('/lead-activities', {
    lead_id: Number(leadId),
    type,
    note: note || null,
    status: when ? 'PENDING' : 'COMPLETED',
    scheduled_at: when ? when.toISOString() : null,
    response_type: outcome,
  });
}

// ---- stages -----------------------------------------------------------------

export const moveLeadToStage = (leadId, stageId) => axiosInstance.post(`/lead-workflow/${leadId}/stage`, { stage_id: Number(stageId) });

export async function leadPipelineStages(leadId) {
  const response = await axiosInstance.get(`/lead-workflow/${leadId}/workflow/options`);
  const data = response.data?.data;
  return Array.isArray(data) ? data : data?.stages || [];
}

// Open stages run light to dark brand blue; won is green, lost is grey. Used by
// the board, pills, bars and the lead stepper so a stage keeps one colour.
const OPEN_TONES = ['#8FCBEA', '#62B4E0', '#3A9BD3', '#1F86C2', '#147DB5', '#0F6392', '#0B4E75'];
export function stageTone(stage, openIndex = 0) {
  if (stage?.is_closed) return Number(stage.probability) >= 100 ? { color: '#2FA984', soft: '#E8F5EF', ink: '#1B7A5A', kind: 'won' } : { color: '#A9B6C0', soft: '#EEF2F4', ink: '#5B7180', kind: 'lost' };
  const color = OPEN_TONES[Math.min(OPEN_TONES.length - 1, Math.max(0, openIndex))];
  return { color, soft: '#EAF4FA', ink: '#0F6392', kind: 'open' };
}
export function stageTones(stages = []) {
  let open = 0;
  return Object.fromEntries(stages.map((stage) => [stage.id, stageTone(stage, stage.is_closed ? 0 : open++)]));
}

// ---- time -------------------------------------------------------------------

export function endOfDay(date) { const d = new Date(date); d.setHours(23, 59, 59, 999); return d; }
export function startOfDay(date) { const d = new Date(date); d.setHours(0, 0, 0, 0); return d; }
const DAY = 86400000;

export function dueBucket(value, now = new Date()) {
  if (!value) return 'nodate';
  const at = new Date(value).getTime();
  const today = startOfDay(now).getTime();
  if (at < now.getTime() && at < today) return 'overdue';
  if (at < today + DAY) return at < now.getTime() ? 'overdue' : 'today';
  if (at < today + 2 * DAY) return 'tomorrow';
  if (at < today + 7 * DAY) return 'week';
  return 'later';
}

const clock = (date) => date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }).replace(/\s?(am|pm)$/i, (m) => ` ${m.trim().toUpperCase()}`);

export function dueLabel(value, now = new Date()) {
  if (!value) return 'No date';
  const date = new Date(value);
  const diff = date.getTime() - now.getTime();
  if (diff < 0) {
    const mins = Math.round(-diff / 60000);
    if (mins < 60) return `${mins} min late`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours} h late`;
    const days = Math.round(hours / 24);
    return `${days} ${days === 1 ? 'day' : 'days'} late`;
  }
  const bucket = dueBucket(value, now);
  if (bucket === 'today') return `Today, ${clock(date)}`;
  if (bucket === 'tomorrow') return `Tomorrow, ${clock(date)}`;
  return `${date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}, ${clock(date)}`;
}

export function quickTimes(now = new Date()) {
  const inHour = new Date(now); inHour.setHours(inHour.getHours() + 1, Math.ceil(inHour.getMinutes() / 15) * 15, 0, 0);
  const evening = new Date(now); evening.setHours(17, 0, 0, 0);
  const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1); tomorrow.setHours(10, 0, 0, 0);
  const nextWeek = new Date(now); nextWeek.setDate(nextWeek.getDate() + ((8 - nextWeek.getDay()) % 7 || 7)); nextWeek.setHours(10, 0, 0, 0);
  const options = [{ id: 'hour', label: 'In 1 hour', at: inHour }];
  if (evening.getTime() - now.getTime() > 90 * 60000) options.push({ id: 'evening', label: 'This evening', at: evening });
  options.push({ id: 'tomorrow', label: 'Tomorrow 10 AM', at: tomorrow }, { id: 'week', label: 'Next Monday', at: nextWeek });
  return options;
}

export function toLocalInput(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// ---- contact ----------------------------------------------------------------

export const telHref = (phone) => (phone ? `tel:${String(phone).replace(/[^\d+]/g, '')}` : null);
export function whatsappHref(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return null;
  return `https://wa.me/${digits.length === 10 ? `91${digits}` : digits}`;
}

export const apiMessage = (error, fallback) => error?.response?.data?.message
  || Object.values(error?.response?.data?.errors || {}).flat()[0]
  || fallback;
